import classNames from "classnames";
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  isWithinInterval,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";
import { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ReactComponent as ArrowSVG } from "assets/images/common/arrow.svg";
import { ReactComponent as CalendarSVG } from "assets/images/common/calendar.svg";

import Button from "components/Button";

import styles from "./style.module.scss";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const VALUE_FORMAT = "yyyy-MM-dd";
const DISPLAY_FORMAT = "dd.MM.yyyy";
const DROPDOWN_HEIGHT = 420;

export type DateRangeValue = {
  from: string | null;
  to: string | null;
};

type Draft = {
  from: Date | null;
  to: Date | null;
};

type DropdownLayout = {
  top: number;
  left: number;
  position: "fixed" | "absolute";
};

type PresetKey = "today" | "yesterday" | "lastWeek" | "lastMonth" | "lastQuarter";

type PropTypes = {
  value?: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
};

const parseValue = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = parse(value, VALUE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

const buildPreset = (key: PresetKey): Draft => {
  const today = startOfDay(new Date());
  switch (key) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const yesterday = subDays(today, 1);
      return { from: yesterday, to: yesterday };
    }
    case "lastWeek":
      return { from: subDays(today, 6), to: today };
    case "lastMonth":
      return { from: subDays(today, 29), to: today };
    case "lastQuarter":
      return { from: subDays(today, 90), to: today };
    default:
      return { from: null, to: null };
  }
};

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "lastWeek", label: "Last week" },
  { key: "lastMonth", label: "Last month" },
  { key: "lastQuarter", label: "Last quarter" },
];

const sameDraft = (a: Draft, b: Draft) =>
  ((a.from === null && b.from === null) || (!!a.from && !!b.from && isSameDay(a.from, b.from))) &&
  ((a.to === null && b.to === null) || (!!a.to && !!b.to && isSameDay(a.to, b.to)));

const DateRangePicker: FC<PropTypes> = ({ value, onChange, placeholder = "Date range", isDisabled, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);

  const committedFrom = useMemo(() => parseValue(value?.from), [value?.from]);
  const committedTo = useMemo(() => parseValue(value?.to), [value?.to]);

  const [draft, setDraft] = useState<Draft>({ from: committedFrom, to: committedTo });
  const [viewMonth, setViewMonth] = useState<Date>(() => committedFrom ?? new Date());
  const [view, setView] = useState<"days" | "months">("days");
  const [monthViewYear, setMonthViewYear] = useState<number>(() => (committedFrom ?? new Date()).getFullYear());
  const [hoverDay, setHoverDay] = useState<Date | null>(null);

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [layout, setLayout] = useState<DropdownLayout | null>(null);

  const displayValue =
    committedFrom && committedTo
      ? `${format(committedFrom, DISPLAY_FORMAT)} – ${format(committedTo, DISPLAY_FORMAT)}`
      : "";

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewMonth]);

  const handleToggle = useCallback(() => {
    if (isDisabled) return;
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        setDraft({ from: committedFrom, to: committedTo });
        setViewMonth(committedFrom ?? new Date());
        setView("days");
        setHoverDay(null);
      }
      return next;
    });
  }, [isDisabled, committedFrom, committedTo]);

  const handleSelectDay = useCallback((day: Date) => {
    setDraft((prev) => {
      if (!prev.from || prev.to) return { from: day, to: null };
      if (isBefore(day, prev.from)) return { from: day, to: prev.from };
      return { from: prev.from, to: day };
    });
  }, []);

  const handlePreset = useCallback((key: PresetKey) => {
    const next = buildPreset(key);
    setDraft(next);
    const target = next.to ?? next.from;
    if (target) setViewMonth(target);
  }, []);

  const handlePrevMonth = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }, []);

  const handleNextMonth = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }, []);

  const handleOpenMonthView = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setMonthViewYear(viewMonth.getFullYear());
      setView("months");
    },
    [viewMonth],
  );

  const handlePrevYear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMonthViewYear((y) => y - 1);
  }, []);

  const handleNextYear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMonthViewYear((y) => y + 1);
  }, []);

  const handleSelectMonth = useCallback(
    (monthIndex: number) => {
      setViewMonth(new Date(monthViewYear, monthIndex, 1));
      setView("days");
    },
    [monthViewYear],
  );

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleApply = useCallback(() => {
    if (draft.from) {
      const to = draft.to ?? draft.from;
      onChange({ from: format(draft.from, VALUE_FORMAT), to: format(to, VALUE_FORMAT) });
    }
    setOpen(false);
  }, [draft, onChange]);

  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const dialogEl = ref.current.closest("dialog");
    const dropdown = dropdownRef.current;
    const dropdownHeight = dropdown?.offsetHeight ?? DROPDOWN_HEIGHT;
    const dropdownWidth = dropdown?.offsetWidth ?? rect.width;
    const GAP = 8;
    const MARGIN = 8;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    let topViewport: number;
    if (dropdownHeight + GAP <= spaceBelow) {
      topViewport = rect.bottom + GAP;
    } else if (dropdownHeight + GAP <= spaceAbove) {
      topViewport = rect.top - dropdownHeight - GAP;
    } else {
      topViewport = window.innerHeight - dropdownHeight - MARGIN;
    }
    if (topViewport < MARGIN) topViewport = MARGIN;

    let leftViewport = rect.left;
    if (leftViewport + dropdownWidth > window.innerWidth - MARGIN) {
      leftViewport = window.innerWidth - dropdownWidth - MARGIN;
    }
    if (leftViewport < MARGIN) leftViewport = MARGIN;

    if (dialogEl) {
      const dRect = dialogEl.getBoundingClientRect();
      setPortalTarget(dialogEl);
      setLayout({ top: topViewport - dRect.top, left: leftViewport - dRect.left, position: "absolute" });
    } else {
      setPortalTarget(document.body);
      setLayout({ top: topViewport, left: leftViewport, position: "fixed" });
    }
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setLayout(null);
      setPortalTarget(null);
      return;
    }
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(raf);
  }, [isOpen, view, draft, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const today = new Date();
  const startToday = startOfDay(today);
  const isSelecting = !!draft.from && !draft.to;
  const previewEnd = isSelecting && hoverDay && draft.from && isAfter(hoverDay, draft.from) ? hoverDay : null;
  const rangeStart = draft.from;
  const rangeEnd = draft.to ?? previewEnd;
  const activePreset = PRESETS.find(({ key }) => sameDraft(buildPreset(key), draft))?.key ?? null;

  const getDayState = (day: Date) => {
    if (!rangeStart) return { inRange: false, isStart: false, isEnd: false, isSingle: false };
    if (!rangeEnd) {
      const isStart = isSameDay(day, rangeStart);
      return { inRange: isStart, isStart, isEnd: isStart, isSingle: isStart };
    }
    const inRange = isWithinInterval(startOfDay(day), { start: startOfDay(rangeStart), end: startOfDay(rangeEnd) });
    const isStart = isSameDay(day, rangeStart);
    const isEnd = isSameDay(day, rangeEnd);
    return { inRange, isStart, isEnd, isSingle: isStart && isEnd };
  };

  return (
    <div ref={ref} className={classNames(styles.wrapper, className, { [styles.disabled]: isDisabled })}>
      <div
        className={classNames(styles.field, { [styles.filled]: !!displayValue, [styles.open]: isOpen })}
        onClick={handleToggle}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        tabIndex={isDisabled ? -1 : 0}
      >
        <span className={classNames(styles.value, { [styles.placeholder]: !displayValue })}>
          {displayValue || placeholder}
        </span>
        <span className={styles.icon}>
          <CalendarSVG />
        </span>
      </div>

      {isOpen &&
        layout &&
        portalTarget &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={{ position: layout.position, top: layout.top, left: layout.left }}
          >
            <div className={styles.presets}>
              <span className={styles.presetsTitle}>Period</span>
              <div className={styles.presetsList}>
                {PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    className={classNames(styles.preset, { [styles.presetActive]: activePreset === preset.key })}
                    onClick={() => handlePreset(preset.key)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.calendar}>
              {view === "days" ? (
                <>
                  <div className={styles.calendarHeader}>
                    <div className={styles.monthTitle}>
                      <button
                        type="button"
                        className={styles.monthLabel}
                        onClick={handleOpenMonthView}
                        aria-label="Select month"
                      >
                        {format(viewMonth, "LLLL")}
                      </button>
                      <span className={styles.monthYear}>{format(viewMonth, "yyyy")}</span>
                    </div>
                    <div className={styles.navGroup}>
                      <button
                        type="button"
                        className={styles.navButton}
                        onClick={handlePrevMonth}
                        aria-label="Previous month"
                      >
                        <ArrowSVG />
                      </button>
                      <button
                        type="button"
                        className={classNames(styles.navButton, styles.navButtonNext)}
                        onClick={handleNextMonth}
                        aria-label="Next month"
                      >
                        <ArrowSVG />
                      </button>
                    </div>
                  </div>

                  <div className={styles.weekdays}>
                    {WEEKDAYS.map((day) => (
                      <span key={day} className={styles.weekday}>
                        {day}
                      </span>
                    ))}
                  </div>

                  <div className={styles.grid}>
                    {days.map((day) => {
                      const { inRange, isStart, isEnd, isSingle } = getDayState(day);
                      const outside = !isSameMonth(day, viewMonth);
                      const disabledDay = isAfter(startOfDay(day), startToday);
                      return (
                        <div
                          key={day.toISOString()}
                          className={classNames(styles.cell, {
                            [styles.inRange]: inRange,
                            [styles.rangeStart]: isStart && !isSingle,
                            [styles.rangeEnd]: isEnd && !isSingle,
                            [styles.rangeSingle]: isSingle,
                          })}
                        >
                          <button
                            type="button"
                            disabled={disabledDay}
                            className={classNames(styles.day, {
                              [styles.dayOutside]: outside,
                              [styles.dayEndpoint]: isStart || isEnd,
                              [styles.dayToday]: !inRange && isToday(day),
                            })}
                            onClick={() => handleSelectDay(day)}
                            onMouseEnter={() => !disabledDay && setHoverDay(day)}
                          >
                            {day.getDate()}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.calendarHeader}>
                    <span className={styles.yearLabel}>{monthViewYear}</span>
                    <div className={styles.navGroup}>
                      <button
                        type="button"
                        className={styles.navButton}
                        onClick={handlePrevYear}
                        aria-label="Previous year"
                      >
                        <ArrowSVG />
                      </button>
                      <button
                        type="button"
                        className={classNames(styles.navButton, styles.navButtonNext)}
                        onClick={handleNextYear}
                        aria-label="Next year"
                      >
                        <ArrowSVG />
                      </button>
                    </div>
                  </div>

                  <div className={styles.monthsGrid}>
                    {MONTHS.map((monthName, index) => {
                      const selectedMonth = monthViewYear === viewMonth.getFullYear() && index === viewMonth.getMonth();
                      const currentMonth = monthViewYear === today.getFullYear() && index === today.getMonth();
                      return (
                        <button
                          key={monthName}
                          type="button"
                          className={classNames(styles.month, {
                            [styles.monthSelected]: selectedMonth,
                            [styles.monthToday]: !selectedMonth && currentMonth,
                          })}
                          onClick={() => handleSelectMonth(index)}
                        >
                          {monthName}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <div className={styles.footer}>
                <Button variant="secondary" className={styles.footerButton} onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className={styles.footerButton}
                  onClick={handleApply}
                  disabled={!draft.from}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>,
          portalTarget,
        )}
    </div>
  );
};

export default memo(DateRangePicker);
